/**
 * Turn a Firebase Auth error into something the person reading it can act on.
 *
 * WHY THIS EXISTS
 *
 * Sign-in used `extractErrorMessage`, which reads `error.response.data.detail`
 * — the shape OUR API returns. A Firebase SDK error has no such field; it
 * carries `error.code` (`auth/invalid-credential`, `auth/user-disabled`, …).
 * So every Firebase failure fell through to the same fallback:
 *
 *     "Login failed. Please try again."
 *
 * Wrong password, no such account, disabled user, rate limit, and the service
 * being unreachable were all indistinguishable. During the 2026-08-17 staging
 * outage that single string was the only thing anyone saw while the API was
 * rejecting every token, and it cost hours: "login is broken" and "you typed
 * the wrong password" look identical to the user AND to whoever they report it
 * to.
 *
 * WHAT IS DELIBERATELY *NOT* DISTINGUISHED
 *
 * `auth/user-not-found` and `auth/wrong-password` both render as one message.
 * Splitting them tells an attacker which addresses have accounts, which is the
 * account-enumeration leak `auth/invalid-credential` was introduced to close.
 * Firebase now returns the merged code by default; the older two are mapped the
 * same way so behaviour cannot change with an SDK version.
 *
 * Everything else IS distinguished, because each has a different next step:
 * a disabled account needs an admin, a rate limit needs waiting, and a network
 * failure needs no user action at all.
 *
 * GOOGLE AND MICROSOFT SIGN-IN USE THIS TOO
 *
 * The SSO buttons had the same bug later: every popup failure on staging read
 * "Google sign-in failed. Please try again.", whether the account already
 * used a password, the browser blocked the popup, or Google refused the app.
 * The popup codes below exist for them.
 */

/** Shape of a Firebase Auth error without importing the SDK's types here. */
interface MaybeFirebaseError {
  code?: unknown;
  message?: unknown;
}

/** Longest piece of Firebase's own wording we will put on screen. */
const MAX_DETAIL_LENGTH = 200;

/**
 * The useful part of an `auth/internal-error`, or null when there is none.
 *
 * Firebase wraps whatever the identity provider said as
 * `Firebase: <text> (auth/internal-error).` When Google blocks an app ("access
 * blocked", an unverified consent screen, a disallowed user type) the text is
 * the provider's JSON error, and its `message` is the only place the reason
 * appears. Without it the person sees a generic failure and so do we.
 *
 * Returns null for the bare `Firebase: Error (auth/internal-error).`, since
 * repeating "Error" back to someone tells them nothing.
 */
export function internalErrorDetail(error: unknown): string | null {
  const raw = (error as MaybeFirebaseError | null)?.message;
  if (typeof raw !== 'string') return null;

  const text = raw
    .replace(/^Firebase:\s*/, '')
    .replace(/\s*\(auth\/[\w-]+\)\.?\s*$/, '')
    .trim();
  if (!text || text === 'Error') return null;

  let detail = text;
  if (text.startsWith('{')) {
    try {
      const parsed = JSON.parse(text) as {
        error?: { message?: unknown } | string;
        message?: unknown;
      };
      const inner =
        typeof parsed.error === 'object'
          ? parsed.error.message
          : (parsed.error ?? parsed.message);
      if (typeof inner === 'string' && inner.trim()) detail = inner.trim();
    } catch {
      // Not JSON after all. The raw text is still better than nothing.
    }
  }

  return detail.length > MAX_DETAIL_LENGTH
    ? `${detail.slice(0, MAX_DETAIL_LENGTH)}...`
    : detail;
}

/**
 * True for the code Firebase returns for BOTH a wrong password and an address
 * with no account. Enumeration protection merges them on purpose, so the UI
 * must keep them merged: one neutral message, never a guess at which it was.
 */
export function isCredentialRejection(error: unknown): boolean {
  switch (firebaseErrorCode(error)) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-login-credentials':
      return true;
    default:
      return false;
  }
}

export function firebaseErrorCode(error: unknown): string | null {
  const code = (error as MaybeFirebaseError | null)?.code;
  return typeof code === 'string' && code.startsWith('auth/') ? code : null;
}

/**
 * A readable message for a Firebase auth failure.
 *
 */
export function firebaseAuthMessage(error: unknown, fallback: string): string {
  switch (firebaseErrorCode(error)) {
    // Merged by Firebase precisely so a wrong password and an unknown address
    // cannot be told apart. Keep them merged.
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-login-credentials':
      return 'Incorrect email or password.';

    case 'auth/invalid-email':
      return 'That email address is not valid.';

    case 'auth/user-disabled':
      return 'This account has been disabled. Contact your administrator.';

    case 'auth/too-many-requests':
      return 'Too many attempts. Wait a few minutes, or reset your password.';

    // Not the user's fault and not fixable by retrying the form.
    case 'auth/network-request-failed':
      return 'Could not reach the sign-in service. Check your connection and try again.';

    // Configuration, not credentials. These are the ones that used to hide
    // behind "Login failed" while nobody could sign in at all.
    //
    // Worded for any method, not just email: Google and Microsoft return the
    // same code when their provider is switched off in Identity Platform.
    case 'auth/operation-not-allowed':
      return 'This sign-in method is not enabled for this environment.';
    case 'auth/unauthorized-domain':
      return 'This site is not authorised for sign-in. This is a configuration problem, not your account.';
    case 'auth/invalid-api-key':
    case 'auth/api-key-not-valid-please-pass-a-valid-api-key':
      return 'Sign-in is misconfigured for this environment (invalid API key).';

    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled.';

    // The address already has an account that signs in some other way, most
    // often a password. Firebase will not merge the two on its own, so the
    // person has to go back to the way they signed up. The error does not
    // say which method that is, and Firebase refuses to look it up while
    // email enumeration protection is on, so the message cannot name it.
    case 'auth/account-exists-with-different-credential':
      return 'This email already signs in another way. Use the method you signed up with, such as your email and password.';

    // Nothing reached Google or Microsoft: the browser stopped the window
    // from opening. Retrying without changing a setting fails the same way.
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in window. Allow pop-ups for this site and try again.';

    // An address with no account, through a popup, with client-side sign-up
    // switched off on the Identity Platform project (`disabledUserSignup`).
    //
    // When the provider's sign-up flag is ON the app never shows this: it
    // takes the person to the "Create your account" step instead, and the
    // backend creates the account after verifying the provider's ID token.
    // This is what remains when the flag is OFF (or its config could not be
    // read): there is no way to create the account from here, so the message
    // names the two paths that exist.
    case 'auth/admin-restricted-operation':
      return 'Sign up with your email and password or ask your administrator for an invitation.';

    // The provider refused and Firebase passed its reason through as text.
    case 'auth/internal-error': {
      const detail = internalErrorDetail(error);
      return detail
        ? `The sign-in service returned an error: ${detail}`
        : 'The sign-in service returned an error. Try again, and contact support if it keeps happening.';
    }

    default:
      return fallback;
  }
}

/**
 * True when the failure is ours rather than the user's.
 *
 * Lets the form stop telling someone to check their password when the real
 * problem is that the environment cannot authenticate anybody.
 */
export function isConfigurationFailure(error: unknown): boolean {
  const code = firebaseErrorCode(error);
  return (
    code === 'auth/operation-not-allowed' ||
    code === 'auth/unauthorized-domain' ||
    code === 'auth/invalid-api-key' ||
    code === 'auth/api-key-not-valid-please-pass-a-valid-api-key'
  );
}

/**
 * How a failed SECOND-FACTOR step reads, and whether the person can retry it on
 * the same screen.
 *
 * Kept apart from `firebaseAuthMessage` because the same code means something
 * else here: `auth/too-many-requests` after a wrong code is not "reset your
 * password", and `auth/code-expired` is not an error in the form at all. Every
 * one of these used to fall to "Sign-in expired. Please enter your password
 * again." — which threw away a half-finished sign-in over a mistyped code or a
 * slow SMS, and sent people back to a step they had already passed.
 *
 * `retryable: false` means the held-back sign-in is gone (the resolver or its
 * session expired) and only the password step can restart it.
 */
export function mfaChallengeError(error: unknown): {
  message: string;
  retryable: boolean;
} | null {
  switch (firebaseErrorCode(error)) {
    case 'auth/invalid-verification-code':
    case 'auth/missing-verification-code':
    case 'auth/totp-challenge-timeout':
      return {
        message: 'That code is not right. Check it and try again.',
        retryable: true,
      };
    case 'auth/code-expired':
      return {
        message:
          'That code has expired. Send a new one and enter it right away.',
        retryable: true,
      };
    case 'auth/too-many-requests':
      return {
        message: 'Too many attempts. Wait a few minutes and try again.',
        retryable: true,
      };
    case 'auth/network-request-failed':
      return {
        message:
          'Could not reach the sign-in service. Check your connection and try again.',
        retryable: true,
      };
    case 'auth/quota-exceeded':
      return {
        message:
          'Text-message codes are temporarily unavailable. Try again later, or contact support.',
        retryable: true,
      };
    case 'auth/captcha-check-failed':
    case 'auth/invalid-app-credential':
      return {
        message:
          'The security check did not pass. Reload the page and try again.',
        retryable: true,
      };
    case 'auth/operation-not-allowed':
      return {
        message:
          'This verification method is not enabled for this environment. Contact support.',
        retryable: false,
      };
    case 'auth/invalid-multi-factor-session':
    case 'auth/session-expired':
    case 'auth/user-token-expired':
      return {
        message: 'Sign-in expired. Please enter your password again.',
        retryable: false,
      };
    default:
      return null;
  }
}
